import {
  Controller,
  useFieldArray,
  type Control, useWatch,
} from 'react-hook-form'
import { BaseButton, BaseSelect, BaseInput } from '@/shared/components'
import TrashIcon from '@/shared/assets/icons/trash.svg?react'
import { IncomingTopupChannel } from '@/entities/paymentMethods'
import { LimitModel, PaymentRequisiteModel } from '@/entities/paymentRequisit'
import { useMemo, useRef, useEffect } from 'react'
import { RequisiteModel } from '@/features/requisites'
import { useApiQuery } from '@/api/main/query.ts'
import { topupLimitsAPI } from '@/entities/paymentMethods/api/endpoint.ts'

type Option = { label: string; value: string }

type Props = {
  control: Control<RequisiteModel>
  title?: string
  profiles: PaymentRequisiteModel[]
  disabled?: boolean
}

export function PaymentMethodLimits({
                                      control,
                                      title = 'Лимиты по платёжным методам',
                                      profiles,
                                      disabled,
                                    }: Props) {
  const country = 'ru';
  const currency = 643;

  const {data: typeList} = useApiQuery<IncomingTopupChannel[]>(
    ['incoming-topup-channels', {country, currency}],
    topupLimitsAPI.channels,
    {country, currency}
  );

  // единый «ведущий» массив — лимиты 0-го профиля
  const NAME = 'paymentRequisitProfiles.0.limits' as const

  // 1) соберём сводный список по всем профилям, ключ — topupPaymentChannelId
  const mergedLimits: LimitModel[] = useMemo(() => {
    const maxByChannel = new Map<string, number>()         // channelId -> maxPerDay
    const idFromP0 = new Map<string, string>()             // channelId -> limit.id из профиля 0

    const p0 = profiles?.[0]
    for (const l of p0?.limits ?? []) {
      if (l.topupPaymentChannelId) idFromP0.set(l.topupPaymentChannelId, l.id || '')
    }

    for (const p of profiles ?? []) {
      for (const l of p?.limits ?? []) {
        const ch = l.topupPaymentChannelId
        const v = Number(l.maxPerDay ?? 0)
        maxByChannel.set(ch, Math.max(maxByChannel.get(ch) ?? 0, v))
      }
    }

    return Array.from(maxByChannel, ([topupPaymentChannelId, maxPerDay]) => {
      const id = idFromP0.get(topupPaymentChannelId) ?? `NEW:${topupPaymentChannelId}`
      return {
        id,
        topupPaymentChannelId,
        maxPerDay,
        paymentRequisitId: p0?.id,
        pendingCount: 0,
        successCount: 0,
      } as LimitModel
    })
  }, [profiles])

  // 2) Опции: label — имя метода, value — UUID канала
  const allOptions: Option[] = useMemo(
    () =>
      typeList ? typeList
        .filter(i => i.methodFromName !== 'SbpQr') // По запросу от @vlad_013 удален из списка
        .map((limit) => ({
          label: limit.methodFromName,
          value: limit.id,
        })) : [],
    [typeList]
  )

  // 4) форма — источник истины
  const { fields, append, remove, replace } = useFieldArray<
    RequisiteModel,
    typeof NAME,
    'formKey'
  >({
    control,
    name: NAME,
    keyName: 'formKey',
  })

  // Однократно засеиваем merged в форму (можно убрать guard, если нужно перезасевать при смене profiles)
  const seeded = useRef(false)
  useEffect(() => {
    if (seeded.current) return
    replace(mergedLimits)
    seeded.current = true
  }, [mergedLimits, replace])

  const handleAddLimit = () => {
    const empty: LimitModel = {
      id: '',                         // пустой => новый
      topupPaymentChannelId: '',      // выберут из селекта
      maxPerDay: 0,
      paymentRequisitId: '',
      pendingCount: 0,
      successCount: 0,
    }
    append(empty)
  }

  const watchedLimits = useWatch({
    control,
    name: NAME,
  });

  const selectedIds = useMemo(
    () =>
      new Set(
        (watchedLimits ?? [])
          .map(l => l?.topupPaymentChannelId)
          .filter(Boolean) as string[]
      ),
    [watchedLimits]
  );

  const canAddMore = useMemo(
    () => (allOptions ?? []).some(opt => !selectedIds.has(opt.value)),
    [allOptions, selectedIds]
  );

  if (!profiles?.length) return null;

  return (
    <div className="flex flex-col gap-3">
      <div className="text-base font-medium">{title}</div>

      <div className="flex flex-col gap-2">
        {(fields as LimitModel[]).map((_, index) => {
          const current = watchedLimits?.[index]?.topupPaymentChannelId ?? '';

          const availableOptions = allOptions.filter(
            opt => !selectedIds.has(opt.value) || opt.value === current
          );

          return (
            <div key={(fields)[index].formKey} className="flex items-start gap-3">
              <BaseButton
                variant="ghost"
                size="icon"
                onClick={() => remove(index)}
                aria-label="Удалить лимит"
                className="flex-none"
                disabled={disabled}
              >
                <TrashIcon className="text-error" />
              </BaseButton>

              {/* topupPaymentChannelId */}
              <Controller
                name={`${NAME}.${index}.topupPaymentChannelId`}
                control={control}
                rules={{ required: 'Выберите метод' }}
                render={({ field, fieldState }) => (
                  <BaseSelect
                    {...field}
                    label="Метод"
                    placeholder="Выберите"
                    options={availableOptions}
                    error={fieldState.error?.message}
                    className="flex-1"
                  />
                )}
              />

              {/* maxPerDay */}
              <Controller
                name={`${NAME}.${index}.maxPerDay`}
                control={control}
                render={({ field, fieldState }) => (
                  <BaseInput
                    {...field}
                    label="Лимит"
                    placeholder="Введите"
                    type="number"
                    variant="secondary"
                    step={1}
                    error={fieldState.error?.message}
                    className="flex-1"
                    onBlur={undefined}
                  />
                )}
              />
            </div>
          )
        })}
      </div>

      {canAddMore && (
        <BaseButton
          onClick={handleAddLimit}
          variant="ghost"
          iconPosition="left"
          className="text-primary dark:text-primary-dark"
          disabled={disabled}
        >
          + Добавить лимит к платёжному методу
        </BaseButton>
      )}
    </div>
  )
}
